import type { ContentTableSortColumn } from './contentSort';

export type ContentMetricKey =
  | 'engagement'
  | 'engagementRate'
  | 'impressions'
  | 'reach'
  | 'reactions'
  | 'plays'
  | 'videoViews'
  | 'shares'
  | 'clicks'
  | 'views'
  | 'likes'
  | 'comments'
  | 'saves'
  | 'profileVisits'
  | 'follows'
  | 'avgWatchTime'
  | 'totalWatchTime'
  | 'replies'
  | 'storyTapsForward'
  | 'storyTapsBack'
  | 'storyExits'
  | 'storySwipeForward';

export interface MetricColumnDef {
  key: ContentMetricKey;
  labelKey: string;
  tooltipKey?: string;
  sortKey?: ContentTableSortColumn;
  width?: number;
}

export const IG_FEED_COLUMNS: MetricColumnDef[] = [
  {
    key: 'engagementRate',
    labelKey: 'analyticsContent.erPercent',
    tooltipKey: 'analyticsContent.igFeedErPercentTableTooltip',
    sortKey: 'engagementRate',
    width: 100,
  },
  {
    key: 'views',
    labelKey: 'analyticsContent.views',
    tooltipKey: 'analyticsContent.igFeedViewsTableTooltip',
    sortKey: 'views',
    width: 100,
  },
  {
    key: 'reach',
    labelKey: 'analyticsContent.reach',
    tooltipKey: 'analyticsContent.igFeedReachTableTooltip',
    sortKey: 'reach',
    width: 100,
  },
  {
    key: 'likes',
    labelKey: 'analyticsContent.likes',
    tooltipKey: 'analyticsContent.igFeedLikesTableTooltip',
    sortKey: 'likes',
    width: 100,
  },
  {
    key: 'comments',
    labelKey: 'analyticsContent.comments',
    tooltipKey: 'analyticsContent.igFeedCommentsTableTooltip',
    sortKey: 'comments',
    width: 100,
  },
  {
    key: 'shares',
    labelKey: 'analyticsContent.shares',
    tooltipKey: 'analyticsContent.igFeedSharesTableTooltip',
    sortKey: 'shares',
    width: 100,
  },
  {
    key: 'saves',
    labelKey: 'analyticsContent.saves',
    tooltipKey: 'analyticsContent.igFeedSavesTableTooltip',
    sortKey: 'saves',
    width: 100,
  },
  {
    key: 'profileVisits',
    labelKey: 'analyticsContent.profileVisits',
    tooltipKey: 'analyticsContent.igFeedProfileVisitsTableTooltip',
    sortKey: 'profileVisits',
    width: 130,
  },
  {
    key: 'follows',
    labelKey: 'analyticsContent.follows',
    tooltipKey: 'analyticsContent.igFeedFollowsTableTooltip',
    sortKey: 'follows',
    width: 100,
  },
];

export const IG_REELS_COLUMNS: MetricColumnDef[] = [
  {
    key: 'views',
    labelKey: 'analyticsContent.views',
    tooltipKey: 'analyticsContent.igReelsViewsTableTooltip',
    sortKey: 'views',
    width: 100,
  },
  {
    key: 'reach',
    labelKey: 'analyticsContent.reach',
    tooltipKey: 'analyticsContent.igReelsReachTableTooltip',
    sortKey: 'reach',
    width: 100,
  },
  {
    key: 'likes',
    labelKey: 'analyticsContent.likes',
    tooltipKey: 'analyticsContent.igReelsLikesTableTooltip',
    sortKey: 'likes',
    width: 100,
  },
  {
    key: 'comments',
    labelKey: 'analyticsContent.comments',
    tooltipKey: 'analyticsContent.igReelsCommentsTableTooltip',
    sortKey: 'comments',
    width: 100,
  },
  {
    key: 'shares',
    labelKey: 'analyticsContent.shares',
    tooltipKey: 'analyticsContent.igReelsSharesTableTooltip',
    sortKey: 'shares',
    width: 100,
  },
  {
    key: 'saves',
    labelKey: 'analyticsContent.saves',
    tooltipKey: 'analyticsContent.igReelsSavesTableTooltip',
    sortKey: 'saves',
    width: 100,
  },
  {
    key: 'avgWatchTime',
    labelKey: 'analyticsContent.metrics.avgWatchTime',
    tooltipKey: 'analyticsContent.igReelsAvgWatchTimeTableTooltip',
    sortKey: 'avgWatchTime',
    width: 140,
  },
  {
    key: 'totalWatchTime',
    labelKey: 'analyticsContent.metrics.totalWatchTime',
    tooltipKey: 'analyticsContent.igReelsTotalWatchTimeTableTooltip',
    sortKey: 'totalWatchTime',
    width: 150,
  },
];

export const IG_STORIES_COLUMNS: MetricColumnDef[] = [
  {
    key: 'views',
    labelKey: 'analyticsContent.views',
    tooltipKey: 'analyticsContent.igStoriesViewsTableTooltip',
    sortKey: 'views',
    width: 100,
  },
  {
    key: 'reach',
    labelKey: 'analyticsContent.reach',
    tooltipKey: 'analyticsContent.igStoriesReachTableTooltip',
    sortKey: 'reach',
    width: 100,
  },
  {
    key: 'replies',
    labelKey: 'analyticsContent.replies',
    tooltipKey: 'analyticsContent.igStoriesRepliesTableTooltip',
    sortKey: 'replies',
    width: 100,
  },
  {
    key: 'shares',
    labelKey: 'analyticsContent.shares',
    tooltipKey: 'analyticsContent.igStoriesSharesTableTooltip',
    sortKey: 'shares',
    width: 100,
  },
  {
    key: 'storyTapsForward',
    labelKey: 'analyticsContent.storyNavForward',
    tooltipKey: 'analyticsContent.igStoriesNavForwardTableTooltip',
    sortKey: 'storyTapsForward',
    width: 170,
  },
  {
    key: 'storyTapsBack',
    labelKey: 'analyticsContent.storyNavBack',
    tooltipKey: 'analyticsContent.igStoriesNavBackTableTooltip',
    sortKey: 'storyTapsBack',
    width: 160,
  },
  {
    key: 'storyExits',
    labelKey: 'analyticsContent.storyNavExit',
    tooltipKey: 'analyticsContent.igStoriesNavExitTableTooltip',
    sortKey: 'storyExits',
    width: 155,
  },
  {
    key: 'storySwipeForward',
    labelKey: 'analyticsContent.storyNavNextStory',
    tooltipKey: 'analyticsContent.igStoriesNavNextStoryTableTooltip',
    sortKey: 'storySwipeForward',
    width: 175,
  },
  {
    key: 'follows',
    labelKey: 'analyticsContent.follows',
    tooltipKey: 'analyticsContent.igStoriesFollowsTableTooltip',
    sortKey: 'follows',
    width: 100,
  },
  {
    key: 'profileVisits',
    labelKey: 'analyticsContent.profileVisits',
    tooltipKey: 'analyticsContent.igStoriesProfileVisitsTableTooltip',
    sortKey: 'profileVisits',
    width: 130,
  },
];

export const FB_POSTS_COLUMNS: MetricColumnDef[] = [
  {
    key: 'engagementRate',
    labelKey: 'analyticsContent.erPercent',
    tooltipKey: 'analyticsContent.fbPostsErPercentTableTooltip',
    sortKey: 'engagementRate',
    width: 100,
  },
  {
    key: 'views',
    labelKey: 'analyticsContent.views',
    tooltipKey: 'analyticsContent.fbPostsViewsTableTooltip',
    sortKey: 'views',
    width: 100,
  },
  {
    key: 'reach',
    labelKey: 'analyticsContent.reach',
    tooltipKey: 'analyticsContent.fbPostsReachTableTooltip',
    sortKey: 'reach',
    width: 100,
  },
  {
    key: 'reactions',
    labelKey: 'analyticsContent.reactions',
    tooltipKey: 'analyticsContent.fbPostsReactionsTableTooltip',
    sortKey: 'reactions',
    width: 110,
  },
  {
    key: 'comments',
    labelKey: 'analyticsContent.comments',
    tooltipKey: 'analyticsContent.fbPostsCommentsTableTooltip',
    sortKey: 'comments',
    width: 100,
  },
  {
    key: 'shares',
    labelKey: 'analyticsContent.shares',
    tooltipKey: 'analyticsContent.fbPostsSharesTableTooltip',
    sortKey: 'shares',
    width: 100,
  },
  {
    key: 'clicks',
    labelKey: 'analyticsContent.clicks',
    tooltipKey: 'analyticsContent.fbPostsClicksTableTooltip',
    sortKey: 'clicks',
    width: 100,
  },
];

export const FB_REELS_COLUMNS: MetricColumnDef[] = [
  {
    key: 'plays',
    labelKey: 'analyticsContent.plays',
    tooltipKey: 'analyticsContent.fbReelsPlaysTableTooltip',
    sortKey: 'plays',
    width: 100,
  },
  {
    key: 'reach',
    labelKey: 'analyticsContent.reach',
    tooltipKey: 'analyticsContent.fbReelsReachTableTooltip',
    sortKey: 'reach',
    width: 100,
  },
  {
    key: 'avgWatchTime',
    labelKey: 'analyticsContent.metrics.avgWatchTime',
    tooltipKey: 'analyticsContent.fbReelsAvgWatchTimeTableTooltip',
    sortKey: 'avgWatchTime',
    width: 140,
  },
  {
    key: 'totalWatchTime',
    labelKey: 'analyticsContent.metrics.totalWatchTime',
    tooltipKey: 'analyticsContent.fbReelsTotalWatchTimeTableTooltip',
    sortKey: 'totalWatchTime',
    width: 150,
  },
  {
    key: 'reactions',
    labelKey: 'analyticsContent.reactions',
    tooltipKey: 'analyticsContent.fbReelsReactionsTableTooltip',
    sortKey: 'reactions',
    width: 110,
  },
  {
    key: 'comments',
    labelKey: 'analyticsContent.comments',
    tooltipKey: 'analyticsContent.fbReelsCommentsTableTooltip',
    sortKey: 'comments',
    width: 100,
  },
  {
    key: 'shares',
    labelKey: 'analyticsContent.shares',
    tooltipKey: 'analyticsContent.fbReelsSharesTableTooltip',
    sortKey: 'shares',
    width: 100,
  },
];

export const TIKTOK_VIDEO_COLUMNS: MetricColumnDef[] = [
  {
    key: 'videoViews',
    labelKey: 'analyticsContent.metrics.videoViews',
    tooltipKey: 'analyticsContent.metrics.videoViewsTooltip',
    sortKey: 'videoViews',
    width: 120,
  },
  {
    key: 'engagementRate',
    labelKey: 'analyticsContent.erPercent',
    sortKey: 'engagementRate',
    width: 100,
  },
  {
    key: 'engagement',
    labelKey: 'analyticsContent.metrics.engagement',
    sortKey: 'engagement',
    width: 120,
  },
  {
    key: 'likes',
    labelKey: 'analyticsContent.likes',
    sortKey: 'likes',
    width: 100,
  },
  {
    key: 'comments',
    labelKey: 'analyticsContent.comments',
    sortKey: 'comments',
    width: 100,
  },
  {
    key: 'shares',
    labelKey: 'analyticsContent.shares',
    sortKey: 'shares',
    width: 100,
  },
  {
    key: 'reach',
    labelKey: 'analyticsContent.reach',
    sortKey: 'reach',
    width: 100,
  },
  {
    key: 'avgWatchTime',
    labelKey: 'analyticsContent.metrics.avgWatchTime',
    sortKey: 'avgWatchTime',
    width: 140,
  },
  {
    key: 'totalWatchTime',
    labelKey: 'analyticsContent.metrics.totalWatchTime',
    sortKey: 'totalWatchTime',
    width: 150,
  },
];

export const LI_FEED_COLUMNS: MetricColumnDef[] = [
  {
    key: 'engagement',
    labelKey: 'analyticsContent.liEngagement',
    tooltipKey: 'analyticsContent.liEngagementTableTooltip',
    sortKey: 'engagement',
    width: 140,
  },
  {
    key: 'engagementRate',
    labelKey: 'analyticsContent.erPercent',
    tooltipKey: 'analyticsContent.liErPercentTableTooltip',
    sortKey: 'engagementRate',
    width: 100,
  },
  {
    key: 'impressions',
    labelKey: 'analyticsContent.impressions',
    tooltipKey: 'analyticsContent.liImpressionsTableTooltip',
    sortKey: 'impressions',
    width: 140,
  },
  {
    key: 'reach',
    labelKey: 'analyticsContent.uniqueImpressions',
    tooltipKey: 'analyticsContent.liUniqueImpressionsTableTooltip',
    sortKey: 'reach',
    width: 160,
  },
  {
    key: 'clicks',
    labelKey: 'analyticsContent.clicks',
    tooltipKey: 'analyticsContent.liClicksTableTooltip',
    sortKey: 'clicks',
    width: 100,
  },
  {
    key: 'likes',
    labelKey: 'analyticsContent.likes',
    tooltipKey: 'analyticsContent.liLikesTableTooltip',
    sortKey: 'likes',
    width: 100,
  },
  {
    key: 'comments',
    labelKey: 'analyticsContent.comments',
    tooltipKey: 'analyticsContent.liCommentsTableTooltip',
    sortKey: 'comments',
    width: 100,
  },
  {
    key: 'shares',
    labelKey: 'analyticsContent.shares',
    tooltipKey: 'analyticsContent.liSharesTableTooltip',
    sortKey: 'shares',
    width: 100,
  },
];
